import React, { useState, useEffect } from 'react'
import { IoArrowForwardOutline } from "react-icons/io5";
import api from '../services/api';
import { useTranslation } from 'react-i18next';


const News = ({ isHomepage = false }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('haberler');
    const [newsData, setNewsData] = useState([]);
    const [announcementsData, setAnnouncementsData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Tarih formatlama fonksiyonu
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Cointelegraph'dan RSS feed'i çek
    const fetchRSSFeed = async () => {
        try {
            // Backend API'den RSS feed'i çek
            const response = await api.getRSSFeed();
            
            if (!response.success || !response.data) {
                console.warn('RSS feed alınamadı');
                return [];
            }

            const xmlText = response.data;
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

            // Parse hatalarını kontrol et
            const parserError = xmlDoc.querySelector('parsererror');
            if (parserError) {
                console.error('XML parse hatası:', parserError.textContent);
                return [];
            }

            const items = xmlDoc.querySelectorAll('item');
            
            if (items.length === 0) {
                console.warn('RSS feed\'de haber bulunamadı');
                return [];
            }

            const rssNews = Array.from(items).slice(0, 10).map((item, index) => {
                const title = item.querySelector('title')?.textContent || '';
                const linkElement = item.querySelector('link')?.textContent || '';
                const description = item.querySelector('description')?.textContent || '';
                const pubDate = item.querySelector('pubDate')?.textContent || '';
                const guid = item.querySelector('guid')?.textContent || '';

                // CDATA içinden link çıkar
                const link = linkElement.trim();

                // Açıklamadan HTML etiketlerini temizle
                const cleanDescription = description.replace(/<[^>]*>/g, '').trim();

                return {
                    id: `rss-${index}`,
                    title: title,
                    summary: cleanDescription.substring(0, 150) + (cleanDescription.length > 150 ? '...' : ''),
                    publishedAt: pubDate,
                    link: link,
                    guid: guid,
                    isExternalNews: true // RSS haberlerini tanımlamak için işaret
                };
            });

            return rssNews;
        } catch (error) {
            console.error('RSS feed çekilirken hata oluştu:', error);
            return [];
        }
    };

    // Başlıktan URL dostu slug oluştur
    const createSlug = (title) => {
        // Türkçe karakter eşleştirmesi
        const turkishChars = {
            'ç': 'c', 'ğ': 'g', 'ı': 'i', 'ö': 'o', 'ş': 's', 'ü': 'u',
            'Ç': 'c', 'Ğ': 'g', 'İ': 'i', 'Ö': 'o', 'Ş': 's', 'Ü': 'u'
        };

        return title
            .toLowerCase()
            .replace(/[çğıöşüÇĞİÖŞÜ]/g, (char) => turkishChars[char] || char) // Türkçe karakterleri değiştir
            .replace(/[^a-z0-9\s-]/g, '') // Boşluk ve tire dışındaki özel karakterleri kaldır
            .replace(/\s+/g, '-') // Boşlukları tire ile değiştir
            .replace(/-+/g, '-') // Birden fazla tireyi tek tire ile değiştir
            .trim();
    };

    // Haber ve duyuru verilerini çek
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // RSS feed'i çek
                const rssNews = await fetchRSSFeed();
                setNewsData(rssNews || []);

                // Duyuruları ve promosyonları beraber çek (hata verse bile devam et)
                try {
                    const [annRes, promoRes] = await Promise.all([
                        api.getAllAnnouncements().catch(() => ({ success: false })),
                        api.getAllPromotions().catch(() => ({ success: false }))
                    ]);
                    const announcements = (annRes?.success && annRes.data) ? annRes.data.map(a => ({ ...a, _type: 'announcement' })) : [];
                    const promotions = (promoRes?.success && promoRes.data) ? promoRes.data.map(p => ({ ...p, _type: 'promotion' })) : [];
                    const merged = [...announcements, ...promotions].sort((a, b) => {
                        const da = a.startDate ? new Date(a.startDate).getTime() : 0;
                        const db = b.startDate ? new Date(b.startDate).getTime() : 0;
                        return db - da;
                    });
                    setAnnouncementsData(merged);
                } catch (announcementErr) {
                    console.warn('Duyurular/promosyonlar yüklenemedi:', announcementErr);
                }
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    return (
        <div className='news-content'>
            <div className='container'>
                <div className={`news-header ${isHomepage ? '' : 'news-header-page'}`}>
                    {isHomepage ? (
                        <>
                            <div>
                                <h2 className='mb-2'>{activeTab === 'haberler' ? t('news.news') : t('news.announcements')}</h2>
                                <p className='news-subtitle'>{activeTab === 'haberler' ? t('news.explanation') : t('news.announcementsExplanation')}</p>
                            </div>
                            <ul className="nav news-tabs">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link${activeTab === 'haberler' ? ' active' : ''}`}
                                        onClick={() => setActiveTab('haberler')}
                                        type="button"
                                    >
                                        {t('news.news')}
                                    </button>
                                </li>
                                <li className="news-tab-separator">/</li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link${activeTab === 'duyurular' ? ' active' : ''}`}
                                        onClick={() => setActiveTab('duyurular')}
                                        type="button"
                                    >
                                        {t('news.announcements')}
                                    </button>
                                </li>
                            </ul>
                        </>
                    ) : (
                        <>
                            <div>
                                <h2 className='mb-2'>{activeTab === 'haberler' ? t('news.news') : t('news.announcements')}</h2>
                                <p className='news-subtitle'>{activeTab === 'haberler' ? t('news.explanation') : t('news.announcementsExplanation')}</p>
                            </div>
                            <ul className="nav news-tabs">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link${activeTab === 'haberler' ? ' active' : ''}`}
                                        onClick={() => setActiveTab('haberler')}
                                        type="button"
                                    >
                                        {t('news.news')}
                                    </button>
                                </li>
                                <li className="news-tab-separator">/</li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link${activeTab === 'duyurular' ? ' active' : ''}`}
                                        onClick={() => setActiveTab('duyurular')}
                                        type="button"
                                    >
                                        {t('news.announcements')}
                                    </button>
                                </li>
                            </ul>
                        </>
                    )}
                </div>

                <div className='news-body'>
                    {loading ? (
                        <div className='text-center py-4'>
                            <div className='spinner-border' role='status'>
                                <span className='visually-hidden'>Yükleniyor...</span>
                            </div>
                            <p className='mt-2'>Haberler yükleniyor...</p>
                        </div>
                    ) : (activeTab === 'haberler' ? newsData : announcementsData).length === 0 ? (
                        <div className='text-center py-4'>
                            <p className='text-muted'>
                                {activeTab === 'haberler' ? 'Şu anda gösterilecek haber bulunmamaktadır.' : 'Şu anda gösterilecek duyuru bulunmamaktadır.'}
                            </p>
                        </div>
                    ) : (
                        (activeTab === 'haberler' ? newsData : announcementsData)
                            .slice(0, isHomepage ? 3 : undefined)
                            .map((item, index) => (
                            <a
                                key={`${activeTab}-${index}`}
                                target={item.isExternalNews ? '_blank' : '_self'}
                                rel={item.isExternalNews ? 'noopener noreferrer' : ''}
                                href={item.isExternalNews ? item.link : (activeTab === 'haberler' ? `/news/${createSlug(item.title)}/${item.id}` : `/${item._type === 'promotion' ? 'promotions' : 'announcements'}/${createSlug(item.title)}/${item.id}`)}
                                className='news-body-item d-flex align-items-center justify-content-between'
                            >
                                <div>
                                    <h5 className='mb-1 news-title'>
                                        {item.title}
                                    </h5>
                                    <span className='news-summary'>
                                        {activeTab === 'haberler' ? formatDate(item.publishedAt) : formatDate(item.startDate)}
                                    </span>
                                    {activeTab === 'haberler' && item.summary && (
                                        <p className='mt-2 small' style={{ color: 'var(--muted)' }}>
                                            {item.summary}
                                        </p>
                                    )}
                                </div>
                                <div className='d-flex align-items-center'>
                                    <div className='ms-4 news-arrow'>
                                        <IoArrowForwardOutline size={22} />
                                    </div>
                                </div>
                            </a>
                        ))
                    )}
                </div>
                
                {isHomepage && !loading && newsData.length > 0 && (
                    <div className='text-center mt-4'>
                        <a 
                            href='/news' 
                            className='btn-view-all-news'
                        >
                            {t('news.viewAllNews') || 'Tüm Haberleri Gör'} →
                        </a>
                    </div>
                )}
            </div>
        </div>
    )
}

export default News